import React, { FC, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { defaultDateFormat } from "@/core/date";
import { sanitizeHtml } from "@/core/security";

type ConversationMessagePreviewProps = {
    conversation: MyBrokerApi.Conversation;
    onMessagePreview?: (conversation: MyBrokerApi.Conversation) => void;
    selectedConversation: MyBrokerApi.Conversation;
};

const ConversationMessagePreview: FC<ConversationMessagePreviewProps> = ({
    conversation,
    onMessagePreview,
    selectedConversation,
}) => {
    const ref = React.createRef<HTMLDivElement>();
    const queryClient = useQueryClient();

    const { t } = useTranslation();

    const getLastMessage = () => {
        return conversation?.messages[conversation?.messages.length - 1];
    };
    const isSelected = selectedConversation?.id === conversation?.id;
    const isRead = getLastMessage()?.status === "read" || isSelected;

    useEffect(() => {
        if (conversation != null && isSelected && !isRead) {
            setTimeout(() => {
                queryClient.invalidateQueries("conversations");
            }, 3000);
        }
        if (isSelected) {
            ref.current.scrollIntoView({
                behavior: "auto",
                block: "start",
            });
        }
    }, [conversation]);

    const handleOnClick = () => {
        if (onMessagePreview) {
            onMessagePreview(conversation);
        }
    };

    return (
        <div
            ref={ref}
            className={`conversation-preview-container${isSelected ? " selected" : ""}${isRead ? "" : " unread"}`}
            data-testid={`conversation-preview-${conversation.id}`}
            onClick={handleOnClick}
        >
            <div className="d-flex align-center conversation-preview-title-container">
                {isRead ? <span className="unreadSpacer" /> : <span className="unreadDot" />}
                {conversation?.isArchived && <span className="archive">{t("Common.Archive")}</span>}
                {conversation?.isReadonly && <span className="readonly">{t("Common.Info")}</span>}
                <div className="conversation-preview-title highlightText"> {conversation.topic}</div>
                <div className="conversation-preview-date"> {defaultDateFormat(getLastMessage()?.timestamp)}</div>
            </div>

            <div
                className="conversation-preview-message"
                dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(getLastMessage()?.body),
                }}
            />
        </div>
    );
};

export default ConversationMessagePreview;
